import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { DropTarget } from 'react-dnd'
import _ from 'lodash'
import log from 'loglevel'

import config from '@config'

import ScrollableContent from '../../../../components/Workspace/components/ScrollableContent'
import ConnectionChecker from '../../../../../../components/ConnectionChecker'
import EntityHeader from '../../../../components/EntityHeader'

import { DND_TYPE } from '../../../../../../dnd/constants'

import { LOCALE_PREFIX } from './constants'
import hocConnect from './hocConnect'

import './style.scss'

const MODULE_NAME = config.modules.report.key
const BLOCKS = config.modules.report.blocks

const blockAccessRightsPathFn = (state, blockKey) => {
    const activeReportType = state.getIn(['ui', 'modules', MODULE_NAME, 'ribbon', 'reports', 'active'], null)
    return ['authorization', 'accessRights', MODULE_NAME, 'custom', 'reports', activeReportType, 'blocks', blockKey]
}

const blockComponents = _.mapValues(BLOCKS, (block) => {
    let component = null
    try {
        // eslint-disable-next-line import/no-dynamic-require, global-require
        component = require(`./components/Blocks/${block.component}`).default
    } catch (error) {
        log.warn(`Missing Report Workspace Block component ${block.key}`)
    }

    return component
})

const workspaceTarget = {
    drop: (props, monitor) => {
        const { openReportById } = props

        const entity = monitor.getItem()

        openReportById(_.toString(entity.entity.get('entId')))
    },
}

function collect(connect) {
    return {
        connectDropTarget: connect.dropTarget(),
    }
}

@hocConnect
@DropTarget(DND_TYPE.ENTITY, workspaceTarget, collect)
export default class Workspace extends Component {
    static propTypes = {
        activeReportId: PropTypes.string,
        connectDropTarget: PropTypes.func.isRequired,
        hasToolbar: PropTypes.bool,
        openReportById: PropTypes.func.isRequired,
        queryStringId: PropTypes.string,
        reportData: PropTypes.object.isRequired,
        sortedBlocks: PropTypes.array.isRequired,
        showReportIndicator: PropTypes.string,
    }

    static defaultProps = {
        activeReportId: null,
        queryStringId: null,
    }

    constructor(props) {
        super(props)

        this._blocks = {}

        this.state = {
            visibleBlocks: {}, // required for navigation highlighting (cannot be in redux state due to performance issues)
        }
    }

    componentDidMount() {
        const { openReportById, queryStringId } = this.props

        if (queryStringId) openReportById(_.toString(queryStringId))
    }

    shouldComponentUpdate(nextProps) {
        if (this.props.activeReportId !== nextProps.activeReportId) return true
        if (_.isEqual(this.props.reportData, nextProps.reportData)) return true
        if (!_.isEqual(this.props.sortedBlocks, nextProps.sortedBlocks)) return true
        if (this.props.queryStringId !== nextProps.queryStringId) return true

        return false
    }

    isBlockInView = (inView, blockKey) => {
        const { visibleBlocks } = this.state

        this.setState({ visibleBlocks: { ...visibleBlocks, [blockKey]: inView } })
    }

    renderBlocks = () => {
        const { activeReportId, sortedBlocks } = this.props

        return _.reduce(
            sortedBlocks,
            (acc, blockKey) => {
                const BlockComponent = blockComponents[blockKey]

                if (!BlockComponent) return acc

                return [
                    ...acc,
                    <div key={blockKey} className={'block-wrap'} ref={(r) => (this._blocks[blockKey] = r)}>
                        <BlockComponent
                            key={blockKey}
                            blockKey={blockKey}
                            id={activeReportId}
                            blockAccessRightsPathFn={blockAccessRightsPathFn}
                            isBlockInView={this.isBlockInView}
                        />
                    </div>,
                ]
            },
            []
        )
    }

    renderReport = () => {
        const { activeReportId, reportData, hasToolbar, showReportIndicator } = this.props
        const { visibleBlocks } = this.state

        const showReport = showReportIndicator !== 'N'

        return (
            <div className={`report id-${activeReportId}`}>
                <div className={'header'}>
                    <EntityHeader hasToolbar={hasToolbar} moduleKey={MODULE_NAME} entity={reportData.entity} />
                </div>
                <div className={'content'}>
                    {showReport ? (
                        <ScrollableContent
                            id={`${MODULE_NAME}_${activeReportId}`}
                            entId={activeReportId}
                            scrollRefs={this._blocks}
                            moduleKey={MODULE_NAME}
                            visibleBlocks={visibleBlocks}
                        >
                            {this.renderBlocks()}
                        </ScrollableContent>
                    ) : (
                        <div className={'no-selected-report'}>
                            <span className={'main-header'}>
                                <FormattedMessage id={`${LOCALE_PREFIX}.reportNotAllowed`} />
                            </span>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    renderNoReport = () => (
        <div className={'no-selected-report'}>
            <div className={'no-report-hero-img'} />
            <span className={'main-header'}>
                <FormattedMessage id={`${LOCALE_PREFIX}.noReportHeader`} />
            </span>
            <span className={'secondary-header'}>
                <FormattedMessage id={`${LOCALE_PREFIX}.noReportText`} />
            </span>
            <div className={'no-report-gif'} />
        </div>
    )

    render() {
        const { activeReportId, connectDropTarget } = this.props

        const content = activeReportId !== null ? this.renderReport() : this.renderNoReport()

        return connectDropTarget(
            <div className={`workspace-${MODULE_NAME}`}>
                {content}
                <ConnectionChecker id={'portfolio-connection-checker'} />
            </div>
        )
    }
}
